import Client from "..";
import { Rarity } from "../inventory/BaseItem";
import { CharmData, Charms } from "../inventory/Charms";
import { ItemData, Items } from "../inventory/Items";
import { WheelSection } from "../wheel/WheelSection";
import { PromiseManager } from "./PromiseManager";
import { ActionTrigger, LevelType, OpponentLevel } from "./TimelineTypes";
import { GameEvents } from "./Types";

export const enum Phase {
  IDLE,
  PRE_SPIN,
  SPINNING,
  RESULT,
  POST_RESULT,
  RESULT_CHECK,
  LEVEL_SUCCESS,
  LEVEL_FAILURE,
  STORE,
}

export class StateMachine {
  private animationManager: PromiseManager = new PromiseManager();

  constructor() {
    this.onLevelUp();

    Client.game.events.on(
      GameEvents.WHEEL_SPINNING_STATE_CHANGED,
      (isSpinning: boolean) => isSpinning && this.onSpinning(),
    );
    Client.game.events.on(GameEvents.PHASE_UPDATED, (phase: Phase) => {
      switch (phase) {
        case Phase.IDLE:
          this.onIdle();
          break;
        case Phase.PRE_SPIN:
          this.onPreSpin();
          break;
        case Phase.POST_RESULT:
          this.onPostResult();
          break;
        case Phase.RESULT_CHECK:
          this.onResultCheck();
          break;
        case Phase.LEVEL_SUCCESS:
          this.onLevelSuccess();
          break;
        case Phase.LEVEL_FAILURE:
          this.onLevelFailure();
          break;
        case Phase.STORE:
          this.onStore();
          break;
      }
    });
  }

  public AddItemToPool(item: Items) {
    Client.state.itemPool.AddItem(
      item,
      ItemData[item],
      this.getRarityWeight(ItemData[item].rarity),
    );
  }

  public AddCharmToPool(charm: Charms) {
    Client.state.charmPool.AddItem(
      charm,
      CharmData[charm],
      this.getRarityWeight(CharmData[charm].rarity),
    );
  }

  public ContinueToNextPhase(): boolean {
    if (
      Client.state.phase !== Phase.LEVEL_SUCCESS &&
      Client.state.phase !== Phase.STORE
    )
      return false;

    if (Client.state.phase === Phase.LEVEL_SUCCESS) {
      // Coming from level success - check if next level is a store
      if (Client.state.currentLevelData?.levelType === LevelType.STORE) {
        Client.state.phase = Phase.STORE;
      } else {
        Client.state.phase = Phase.IDLE;
      }
    } else if (Client.state.phase === Phase.STORE) {
      // Coming from store - advance to next level
      Client.state.level += 1;
      this.onLevelUp();
      Client.state.phase = Phase.IDLE;
    }

    return true;
  }

  public SpinWheel() {
    // If we are not in IDLE we cannot spin
    if (Client.state.phase !== Phase.IDLE || Client.state.availableSpins === 0)
      return;

    Client.state.availableSpins -= 1;

    Client.state.phase = Phase.PRE_SPIN;
  }

  private onIdle() {
    Client.state.wheelSpinning = false;
  }

  private onStore() {
    // Ensure wheel is not considered spinning during shop phase
    Client.state.wheelSpinning = false;
  }

  private onPreSpin() {
    // All logic for pre spin goes here
    this.checkLevelActionTriggers(ActionTrigger.PRE_SPIN);

    this.animationManager.RunPromises().then(() => {
      Client.game.events.emit(GameEvents.SPIN_WHEEL);
    });
  }

  private onSpinning() {
    this.checkLevelActionTriggers(ActionTrigger.SPINNING);
    this.animationManager.RunPromises().then(() => {
      Client.state.phase = Phase.SPINNING;
    });
  }

  public OnSpinCompleted(selectedSection: WheelSection) {
    Client.state.phase = Phase.RESULT;

    this.checkLevelActionTriggers(ActionTrigger.RESULT);

    Client.state.charmInventory.items.forEach((charm) => {
      if (!charm) return;
      this.animationManager.AddPromise((resolve) => {
        const charmGui = Client.getActiveScene()!.charmInventory.GetItem(
          charm.uniqueId,
        );

        if (!charmGui) {
          charm?.effects?.forEach((effect, index) => {
            effect.ApplyEffect();
            if (effect.duration === 0) charm.effects?.splice(index, 1);
          });
          if (charm.effects?.length === 0)
            Client.state.charmInventory.RemoveItemFromInventory(
              charm.uniqueId!,
            );
          resolve(true);
        } else {
          Client.getActiveScene()?.tweens.chain({
            targets: charmGui.image,
            tweens: [
              {
                angle: -30,
                duration: 50,
                ease: "Linear",
              },
              {
                angle: 0,
                duration: 50,
                ease: "Linear",
              },
              {
                angle: 30,
                yoyo: true,
                duration: 100,
                scale: (charmGui?.imageInitialScale ?? 1) * 1.3,
                ease: "Linear",
                onComplete: () => {
                  // Apply effects only once when animation completes
                  charm.effects?.forEach((effect, index) => {
                    effect.ApplyEffect();
                    if (effect.duration === 0) charm.effects?.splice(index, 1);
                  });

                  charmGui?.image?.setAngle(0);
                  charmGui?.image?.setScale(charmGui?.imageInitialScale ?? 1);
                  if (charm.effects?.length === 0)
                    Client.state.charmInventory.RemoveItemFromInventory(
                      charm.uniqueId!,
                    );
                  resolve(true);
                },
              },
            ],
          });
        }
      });
    });

    // Handle multiplier logic AFTER charm effects but BEFORE section effects
    if (Client.state.currentLevelHistory.length > 0) {
      const lastSection =
        Client.state.currentLevelHistory[
          Client.state.currentLevelHistory.length - 1
        ];

      if (lastSection.fieldColor === selectedSection.fieldColor) {
        // Colors match: keep current color, increase multiplier
        Client.state.winMultiplierColor = selectedSection.fieldColor;
        Client.state.winMultiplier += 1;
        this.animationManager.AddPromise((resolve) => {
          setTimeout(() => {
            resolve(true);
          }, 600);
        });
      } else {
        // Colors don't match: reset to default brown, reset multiplier to 1
        Client.state.winMultiplier = 1;
        Client.state.winMultiplierColor = 0xac4c3f; // Reset to default brown color when streak breaks
        this.animationManager.AddPromise((resolve) => {
          setTimeout(() => {
            resolve(true);
          }, 600);
        });
      }
    } else {
      // First spin: set color to current section
      Client.state.winMultiplierColor = selectedSection.fieldColor;
    }
    Client.state.currentLevelHistory.push(selectedSection);

    // Trigger all effects on selected section
    const expiredItems: number[] = [];
    selectedSection.effects.forEach((effect, index) => {
      effect.ApplyEffect();
      if (effect.duration === 0) expiredItems.push(index);
    });

    // Clear all expired effects
    expiredItems.forEach((index) => selectedSection.effects.splice(index, 1));

    this.animationManager.RunPromises().then(() => {
      Client.state.win += selectedSection.score;
      Client.state.phase = Phase.POST_RESULT;
    });
  }

  private onPostResult() {
    const delay = 1000;
    if (Client.state.winMultiplier > 1) {
      this.animationManager.AddPromise((resolve) => {
        setTimeout(() => {
          Client.game.events.emit(GameEvents.WIN_MULTIPLIER_APPLIED);
          Client.state.win *= Client.state.winMultiplier;
          resolve(true);
        }, delay);
      });
    }

    this.animationManager.AddPromise((resolve) => {
      setTimeout(() => {
        Client.state.score += Client.state.win;
        Client.state.win = 0;
        resolve(true);
      }, delay);
    });

    this.animationManager.RunPromises().then(() => {
      setTimeout(() => {
        Client.state.phase = Phase.RESULT_CHECK;
      }, 600);
    });
  }

  private onResultCheck() {
    // Check if we have valid level data (not beyond the timeline)
    if (!Client.state.currentLevelData) {
      // No more levels - game completed!
      Client.state.phase = Phase.LEVEL_SUCCESS;
      return;
    }

    if (
      Client.state.score >=
      (Client.state.currentLevelData as OpponentLevel).requiredScore
    ) {
      Client.state.phase = Phase.LEVEL_SUCCESS;
    } else if (Client.state.availableSpins === 0) {
      Client.state.phase = Phase.LEVEL_FAILURE;
    } else {
      this.checkLevelActionTriggers(ActionTrigger.POST_RESULT);
      if (this.animationManager.anyPromise) {
        this.animationManager.AddPromise((resolve) => {
          setTimeout(() => {
            resolve(true);
          }, 700);
        });
      }

      this.animationManager.RunPromises().then(() => {
        Client.state.phase = Phase.IDLE;
      });
    }
  }

  private onLevelSuccess() {
    // Store spins used before resetting for the level report
    const spinsUsed =
      Client.state.maxNumberOfSpins - Client.state.availableSpins;

    // Calculate bonus money based on score performance
    const currentLevelData = Client.state.currentLevelData as OpponentLevel;
    const requiredScore = currentLevelData.requiredScore;
    const achievedScore = Client.state.score;
    const scorePercentage = (achievedScore / requiredScore) * 100;
    const bonusPercentage = Math.max(0, scorePercentage - 100); // Only bonus above 100%
    const bonusMultiplier = Math.floor(bonusPercentage / 10); // 1 money per 10% above required
    const bonusMoney = Math.min(bonusMultiplier, 5); // Cap bonus money at 5

    // Store level report data
    const levelReportData = {
      spinsUsed,
      baseMoney: currentLevelData.rewardMoney,
      bonusMoney,
      requiredScore,
      achievedScore,
    };

    Client.state.money += currentLevelData.rewardMoney + bonusMoney;
    Client.state.level += 1;
    this.onLevelUp();

    // Show level report window with detailed data
    const gameScene = Client.getActiveScene();
    if (gameScene) {
      gameScene.showLevelReport(levelReportData);
    }
  }

  private onLevelFailure() {
    // Create level report data for failure case
    const spinsUsed =
      Client.state.maxNumberOfSpins - Client.state.availableSpins;
    const currentLevelData = Client.state.currentLevelData as OpponentLevel;

    const levelReportData = {
      spinsUsed,
      baseMoney: 0, // No money on failure
      bonusMoney: 0, // No bonus on failure
      requiredScore: currentLevelData.requiredScore,
      achievedScore: Client.state.score,
    };

    // Show level failure report
    const gameScene = Client.getActiveScene();
    if (gameScene) {
      gameScene.showLevelFailure(levelReportData);
    }
  }

  private checkLevelActionTriggers(triggerType: ActionTrigger) {
    if (Client.state.currentLevelData?.actions) {
      Client.state.currentLevelData?.actions
        .filter((action) => action.triggerType === triggerType)
        .forEach((action) => {
          this.animationManager.AddPromise((resolve) => {
            // Put some animation here
            action.Run();
            Client.game.events.emit(GameEvents.LEVEL_ACTION_TRIGGERED);

            setTimeout(() => {
              resolve(true);
            }, 1200); // Temp, remove later - await a bit before continuing
          });
        });
    }
  }

  private onLevelUp() {
    // Reset all game state for the new level
    Client.state.score = 0;
    Client.state.availableSpins = Client.state.maxNumberOfSpins;
    Client.state.currentLevelHistory = [];
    Client.state.winMultiplier = 1; // Reset multiplier for new level
    Client.state.winMultiplierColor = 0xac4c3f; // Reset to default brown color

    // Game jam mode - all items available from level 1
    if (Client.state.level === 1) {
      // Add all items
      this.AddItemToPool(Items.SCREW);
      this.AddItemToPool(Items.SCREW_DRIVER);
      this.AddItemToPool(Items.CAR_BRAKE);
      this.AddItemToPool(Items.DRYWALL);
      this.AddItemToPool(Items.STICKER);
      this.AddItemToPool(Items.SLEDGEHAMMER);
      this.AddItemToPool(Items.COIL);
      this.AddItemToPool(Items.RIVETGUN);
      this.AddItemToPool(Items.NAILCLIPPER);
      this.AddItemToPool(Items.KNIFE);
      this.AddItemToPool(Items.CARJACK);
      this.AddItemToPool(Items.BOXCUTTER);
      this.AddItemToPool(Items.HOLEPUNCHER);

      // Add all charms
      this.AddCharmToPool(Charms.LUCKY_COIN);
      this.AddCharmToPool(Charms.MOLAR);
      this.AddCharmToPool(Charms.PILL);
      this.AddCharmToPool(Charms.LUCKYCLOVER);
      this.AddCharmToPool(Charms.GEAR);
      this.AddCharmToPool(Charms.TIPJAR);
    }
  }

  public GetRarityPrice(rarity: Rarity): number {
    switch (rarity) {
      case Rarity.COMMON:
        return 10;
      case Rarity.UNCOMMON:
        return 20;
      case Rarity.RARE:
        return 30;
      case Rarity.EPIC:
        return 40;
      case Rarity.LEGENDARY:
        return 50;
      default:
        return 0;
    }
  }

  private getRarityWeight(rarity: Rarity): number {
    switch (rarity) {
      case Rarity.COMMON:
        return 50;
      case Rarity.UNCOMMON:
        return 30;
      case Rarity.RARE:
        return 15;
      case Rarity.EPIC:
        return 4;
      case Rarity.LEGENDARY:
        return 1;
      default:
        return 0;
    }
  }
}
